/**
 * HTTP settings client — GET/PUT /api/settings (Firebase ID token).
 * Secrets are rewrite-only; responses never include full secret values.
 */

import { ApiEnvelope } from '../../domain/types';
import {
  AiModelCatalogResponse,
  AiUsageSummary,
  AiUsageWindow,
  ISettingsClient,
  UserSettingsPublic,
  UserSettingsUpdate,
  HttpClientOptions,
} from './types';
import { createHttpTransport, type HttpTransport } from './httpTransport';
import { parseEnvelope } from './parseEnvelope';

export class HttpSettingsClient implements ISettingsClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions = {}) {
    this.transport = createHttpTransport(options);
  }

  async getSettings(): Promise<ApiEnvelope<UserSettingsPublic>> {
    const response = await this.transport.fetch('/api/settings');
    return parseEnvelope<UserSettingsPublic>(response, undefined, 'SETTINGS_HTTP_ERROR');
  }

  async listAiModels(): Promise<ApiEnvelope<AiModelCatalogResponse>> {
    const response = await this.transport.fetch('/api/ai/models');
    return parseEnvelope<AiModelCatalogResponse>(response, undefined, 'AI_MODELS_HTTP_ERROR');
  }

  async getAiUsage(days: AiUsageWindow): Promise<ApiEnvelope<AiUsageSummary>> {
    const response = await this.transport.fetch(`/api/ai/usage?days=${days}`);
    return parseEnvelope<AiUsageSummary>(response, undefined, 'AI_USAGE_HTTP_ERROR');
  }

  async updateSettings(patch: UserSettingsUpdate): Promise<ApiEnvelope<UserSettingsPublic>> {
    const response = await this.transport.fetch('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    return parseEnvelope<UserSettingsPublic>(response, undefined, 'SETTINGS_HTTP_ERROR');
  }
}
